import { ConfirmModal, Loading, Table, TableRowMenuCell } from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateExpenditureBudgetJurisdiction,
  useDeleteExpenditureBudgetJurisdiction,
  useExpenditureBudgetJurisdictions,
  useUpdateExpenditureBudgetJurisdiction,
} from '../../hooks';
import type { ExpenditureBudgetJurisdictionModel } from '../../models';
import {
  DEFAULT_PAGINATION_SETTING,
  fillZeroToExpenditureBudgetJurisdiction,
} from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { ExpenditureBudgetJurisdictionFormDrawer } from './ExpenditureBudgetJurisdictionFormDrawer';

export const ExpenditureBudgetJurisdictionsPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const jurisdictions = useExpenditureBudgetJurisdictions({});
  const { mutateAsync: createMutateAsync } =
    useCreateExpenditureBudgetJurisdiction();
  const { mutateAsync: updateMutateAsync } =
    useUpdateExpenditureBudgetJurisdiction();
  const { mutateAsync: deleteMutateAsync } =
    useDeleteExpenditureBudgetJurisdiction();
  const editPermission = useMasterTablePermission(true);
  const [
    expenditureBudgetJurisdictionDelete,
    setExpenditureBudgetJurisdictionDelete,
  ] = useState<z.infer<typeof ExpenditureBudgetJurisdictionModel> | null>(null);

  if (jurisdictions.isPending || !jurisdictions.data) {
    return <Loading />;
  }

  const selected = jurisdictions.data.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'expenditure-budget-jurisdiction' &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );

  return (
    <>
      <Table
        dataSource={jurisdictions.data}
        rowKey="id"
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: fillZeroToExpenditureBudgetJurisdiction,
          },
          {
            title: t('name'),
            key: 'name',
            dataIndex: 'name',
          },
          {
            title: t('start-fiscal-year'),
            key: 'startYear',
            dataIndex: 'startYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('end-fiscal-year'),
            key: 'endYear',
            dataIndex: 'endYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('created-datetime'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          {
            title: t('updated-datetime'),
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          ...(editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  fixed: 'right' as const,
                  width: TABLE_ROW_MENU_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_ROW_MENU_WIDTH },
                  }),
                  render: (_, record) => (
                    <TableRowMenuCell
                      onEdit={() => {
                        setDrawerState({
                          open: true,
                          type: 'expenditure-budget-jurisdiction',
                          isEditing: true,
                          id: record.id,
                        });
                      }}
                      items={[
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () =>
                            setExpenditureBudgetJurisdictionDelete(record),
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <ExpenditureBudgetJurisdictionFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...selected,
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            jurisdictions.refetch(),
            (async () => {
              setDrawerState({ open: false });
            })(),
          ]);
        }}
      />
      <ConfirmModal
        open={expenditureBudgetJurisdictionDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(expenditureBudgetJurisdictionDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(expenditureBudgetJurisdictionDelete.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditureBudgetJurisdictionDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setExpenditureBudgetJurisdictionDelete(null)}
        skipCheck
      />
    </>
  );
};
